import { getDataTable } from '../../features/visual-analytics/charts/chartDataBindings';
import { convertColumnDataToDisplayString } from '../../features/visual-analytics/helpers/misc';
import { getColumnInfo } from '../analyticsDataSlice';
import { TpCanonicalTypeInfo, TpDataContext, VALTYPES } from '../interfaces';
import {
  TpActiveEnSelectionFilter,
  TpEnSelectionFilterDefinition,
  TpEnSelectionFilterStep,
  TpEnSelectionFilterStepDualCategorical,
  TpEnSelectionFilterTypes,
} from './interfaces';
import { getEnSelectionFilterStep, removeEnSelectionFilterStep, smartRenderEnFilterItems } from './selectionEnFilters';
import { TpUserEnSelectionAction, TpUserEnSelectionActionDualCategorical } from './userSelectionActions';

function getDualCatEnFilterStepKey(dataTableId: string, colId1: string, colId2: string): string {
  return `dualCategoricalFilterStep_${dataTableId}_${colId1}__${colId2}`;
}

export function getDualCategoricalEnFilterStep(
  selectionFilter: TpActiveEnSelectionFilter,
  dataTableId: string,
  colId1: string,
  colId2: string,
): TpEnSelectionFilterStepDualCategorical | null {
  const step = getEnSelectionFilterStep(selectionFilter, getDualCatEnFilterStepKey(dataTableId, colId1, colId2));
  if (!step) return null;
  return step as TpEnSelectionFilterStepDualCategorical;
}

function handleUserAction(
  state: any,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  userSelectionAction: TpUserEnSelectionAction,
) {
  const userSelectionActionDualCategorical = userSelectionAction as TpUserEnSelectionActionDualCategorical;
  const key = getDualCatEnFilterStepKey(
    userSelectionActionDualCategorical.dataTableId,
    userSelectionActionDualCategorical.colId1,
    userSelectionActionDualCategorical.colId2,
  );
  const step = getEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
  let stepDualCat: TpEnSelectionFilterStepDualCategorical;
  if (!step) {
    stepDualCat = {
      type: TpEnSelectionFilterTypes.DUALCATEGORICAL,
      key,
      dataTableId: userSelectionActionDualCategorical.dataTableId,
      colId1: userSelectionActionDualCategorical.colId1,
      colId2: userSelectionActionDualCategorical.colId2,
      selectedCellsList: [
        {
          val1: userSelectionActionDualCategorical.selectedCategoryId1,
          val2: userSelectionActionDualCategorical.selectedCategoryId2,
        },
      ],
    };
    canonicalTypeInfo.selectionFilter.steps.push(stepDualCat);
  } else {
    stepDualCat = step as TpEnSelectionFilterStepDualCategorical;
    const tryFiltered = stepDualCat.selectedCellsList.filter(
      (cell) =>
        cell.val1 != userSelectionActionDualCategorical.selectedCategoryId1 ||
        cell.val2 != userSelectionActionDualCategorical.selectedCategoryId2,
    );
    if (tryFiltered.length < stepDualCat.selectedCellsList.length) {
      // cell was found, we remove it
      stepDualCat.selectedCellsList = tryFiltered;
      if (stepDualCat.selectedCellsList.length == 0) {
        removeEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
      }
    } else {
      // cell was not found, we add it
      stepDualCat.selectedCellsList.push({
        val1: userSelectionActionDualCategorical.selectedCategoryId1,
        val2: userSelectionActionDualCategorical.selectedCategoryId2,
      });
    }
  }
}

function performStep(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
): any[] {
  const stepDualCat = step as TpEnSelectionFilterStepDualCategorical;
  const dataTable = getDataTable(dataContext, stepDualCat.dataTableId);
  const colInfo1 = getColumnInfo(dataTable, stepDualCat.colId1);
  const colInfo2 = getColumnInfo(dataTable, stepDualCat.colId2);
  const itemIdList = dataTable.columnData[dataTable.colItemId];
  const catValList1 = dataTable.columnData[stepDualCat.colId1];
  const catValList2 = dataTable.columnData[stepDualCat.colId2];
  const selectedItemIds = [];

  const cell = stepDualCat.selectedCellsList[0];
  for (let itemIdx = 0; itemIdx < itemIdList.length; itemIdx++) {
    let match = false;
    const val1 = catValList1[itemIdx];
    const val2 = catValList2[itemIdx];
    for (const cell of stepDualCat.selectedCellsList) {
      const cellMatch1 = colInfo1.valueType != VALTYPES.MULTICAT ? cell.val1 == val1 : val1.includes(cell.val1);
      const cellMatch2 = colInfo2.valueType != VALTYPES.MULTICAT ? cell.val2 == val2 : val2.includes(cell.val2);
      if (cellMatch1 && cellMatch2) match = true;
    }
    if (match) selectedItemIds.push(itemIdList[itemIdx]);
  }
  return selectedItemIds;
}

function renderContent(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
  forceString?: boolean,
) {
  const stepDualCat = step as TpEnSelectionFilterStepDualCategorical;
  const dataTable = getDataTable(dataContext, stepDualCat.dataTableId);
  const colInfo1 = getColumnInfo(dataTable, stepDualCat.colId1);
  const colInfo2 = getColumnInfo(dataTable, stepDualCat.colId2);
  const missingValue = '(unknown value)';
  const valuesList = stepDualCat.selectedCellsList.map(
    (cell) =>
      `${convertColumnDataToDisplayString(colInfo1, cell.val1) ?? missingValue}, ${
        convertColumnDataToDisplayString(colInfo2, cell.val2) ?? missingValue
      }`,
  );

  if (forceString) return `${colInfo1.name},${colInfo2.name}=${smartRenderEnFilterItems(valuesList, true)}`;

  return (
    <span>
      <b>
        {colInfo1.name}, {colInfo2.name}
      </b>
      = {smartRenderEnFilterItems(valuesList)}
    </span>
  );
}

export const defEnSelectionFilterDualCategorical: TpEnSelectionFilterDefinition = {
  handleUserAction,
  performStep,
  renderContent,
};
